// Vue
import { createRouter, createWebHistory, type NavigationGuardNext, type RouteLocationNormalized } from "vue-router"
import { i18n, loadLanguageAsync } from "@/i18n"
import * as Sentry from "@sentry/vue"

// Stores
import { useAuthenticationStore } from "@/stores/authentication"
import { useAccountStore } from "@/stores/account"
import { markNovaLoginSessionFromUrlIfPresent } from "@/utils/userActionTracking"
import { useLocalDiffuserStore } from "@/stores/localDiffuser"
import { useSpaceStore } from "@/stores/space"
import { useProfessionalSpace } from "@/stores/professionalSpace"

// Types
import { PageTitleParamsType, type IPageTitleParams } from "@/types/Router.types"
import { ConfigurationState } from "@/static/Configuration"
import { Space } from "@/static/Space"

// Middlewares
import auth from "@/middlewares/auth"
import config from "@/middlewares/config"

// Routes
import configurationRoutes from "./routes/configurationRoutes"
import authenticationRoutes from "./routes/authenticationRoutes"
import errorRoutes from "./routes/errorRoutes"
import establishmentRoutes from "./routes/establishmentRoutes"
import reviewRoutes from "./routes/reviewRoutes"
import knowledgeBaseRoutes from "./routes/knowledgeBaseRoutes"
import solicitationRoutes from "./routes/solicitationRoutes"
import notificationRoutes from "./routes/notificationRoutes"
import dialogueRoutes from "./routes/dialogueRoutes"
import pilotageRoutes from "./routes/pilotageRoutes"
import userRoutes from "./routes/userRoutes"
import onboardingRoutes from "./routes/onboardingRoutes"
import googleRoutes from "./routes/googleRoutes"
import professionalSpaceRoutes from "@/router/routes/professionalSpaceRoutes"
import facebookRoutes from "@/router/routes/facebookRoutes"
import salesRoutes from "@/router/routes/salesRoutes"
import assistantRoutes from "@/router/routes/assistantRoutes"
import localAssistantRoutes from "@/router/routes/localAssistantRoutes"
import supportRoutes from "@/router/routes/supportRoutes"
import augusteRoutes from "@/router/routes/augusteRoutes"

import {useEstablishmentStore} from "@/stores/establishment"
import { getAccessContract, getAccessPlanCode } from "@/utils/contract"

const DIALOGUE_ROUTE_NAMES = [
  "dialogue-home",
  "dialogue-destination-provider-update",
  "dialogue-local-assistant",
]

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: "/essayer-uska",
      name: "landing-prospect",
      component: () => import("@/views/landing/ProspectLanding.vue"),
      meta: {
        hideIntercom: true,
        hidePwaInstallBanner: true,
        title: "landing_prospect.page_title",
      },
    },
    {
      path: "/essayer-uska/patiente",
      name: "landing-prospect-waiter",
      component: () => import("@/views/landing/ProspectWaiter.vue"),
      meta: {
        hideIntercom: true,
        title: "Uska - Preparation du demonstrateur",
      },
    },
    {
      path: "/essayer-uska/patiente-test",
      name: "landing-prospect-waiter-preview",
      component: () => import("@/views/landing/ProspectWaiter.vue"),
      meta: {
        hideIntercom: true,
        title: "Uska - Preview attente",
      },
    },

    // Auth routes before catch-all /:diffuserType (sinon /magic-link matche "home")
    ...authenticationRoutes,

    {
      path: "/:diffuserType?",
      name: "home",
      component: () => import("@/views/HomeView.vue"),
      meta: {
        requiresConfig: true,
        middleware: [auth, config],
      },
    },

    {
      path: "/subscriptions",
      name: "subscription-management",
      redirect: () => {
        const authenticationStore = useAuthenticationStore()
        return authenticationStore.isSale
          ? { name: "sales-contracts-management" }
          : { name: "settings-contracts-establishments" }
      },
      meta: {
        middleware: [auth],
        title: "subscriptions.page_title",
      },
    },

    {
      path: "/contactus",
      name: "app-contact",
      component: () => import("@/views/configuration/Contact.vue"),
    },

    // Demo establishment routes
    {
      path: "/editorial-calendar",
      name: "editorial-calendar",
      component: () => import("@/views/EditorialCalendar.vue"),
      meta: {
        hideIntercom: true,
        hidePwaInstallBanner: true,
        middleware: [auth, config],
        title: "editorial_calendar.page_title",
      },
    },
    {
      path: "/demo-review-responses",
      name: "demo-review-responses",
      component: () => import("@/views/reviews/DemoReviewResponses.vue"),
      meta: {
        hideIntercom: true,
        hidePwaInstallBanner: true,
        middleware: [auth, config],
        title: "demo_review_responses.page_title",
      },
    },

    {
      path: "/checkout",
      name: "checkout-flow",
      component: () => import("@/views/payments/CheckoutFlow.vue"),
      beforeEnter: async (to) => {
        const establishmentId = normalizeRouteQueryParam(to.query.establishmentId)
        if (!establishmentId) {
          return true
        }

        sessionStorage.setItem("requestedEstablishmentId", establishmentId)
        const establishmentStore = useEstablishmentStore()
        await establishmentStore.prepareForCheckoutEstablishment(establishmentId)
        return true
      },
      meta: {
        title: "Souscription Uska",
      },
    },

    // users
    ...userRoutes,

    // onboarding
    ...onboardingRoutes,
    ...configurationRoutes,

    // establishment features
    ...establishmentRoutes,
    ...reviewRoutes,
    ...knowledgeBaseRoutes,
    ...augusteRoutes,
    ...googleRoutes,
    ...facebookRoutes,

    ...solicitationRoutes,
    ...notificationRoutes,

    // espace Mes échanges
    ...dialogueRoutes,

    // espace Ma visibilité sans établissement
    ...pilotageRoutes,

    // sales only
    ...salesRoutes,

    // premium assistant
    ...assistantRoutes,
    ...localAssistantRoutes,

    // staff support inbox
    ...supportRoutes,

    // apalaches
    ...professionalSpaceRoutes,

    // errors
    ...errorRoutes,
  ],
  scrollBehavior() {
    // always scroll to top
    return { top: 0 }
  },
})

router.beforeEach(async (to, from, next) => {
  try {
    await loadLanguages()
  } catch (error) {
    console.error('[Router Guard] Error loading languages:', error)
  }

  // Deep link assistant (email support) : persister dès l'entrée pour survivre aux redirects boot.
  const deepEstablishment = normalizeRouteQueryParam(to.query.establishment)
  const deepConversation = normalizeRouteQueryParam(to.query.conversation)
  if (deepEstablishment) {
    sessionStorage.setItem("requestedEstablishmentId", deepEstablishment)
  }
  if (deepConversation) {
    sessionStorage.setItem("assistantReturnConversationId", deepConversation)
  }
  if (to.name === "assistant" && (deepEstablishment || deepConversation)) {
    sessionStorage.setItem("pendingAssistantDeepLink", "1")
  }

  const dialogueSource = normalizeRouteQueryParam(to.query.source)
  if (
    dialogueSource &&
    (to.path.startsWith("/mes-echanges") ||
      to.path.startsWith("/dialogue") ||
      to.name === "magic-link")
  ) {
    sessionStorage.setItem("requestedDialogueSource", dialogueSource)
  }

  if (to.name === "magic-link") {
    const dialogueSourceFromUrl = normalizeRouteQueryParam(to.query.source)
    if (dialogueSourceFromUrl) {
      sessionStorage.setItem("requestedDialogueSource", dialogueSourceFromUrl)
    }
  }

  // Vérifier si c'est un magic link token et rediriger vers /magic-link (params URL conservés)
  if (to.query.token && to.name !== "magic-link") {
    const token = to.query.token.toString()
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

    if (uuidRegex.test(token)) {
      const query: Record<string, string> = { token }

      const establishmentId = normalizeRouteQueryParam(to.query.establishmentId)
      if (establishmentId) {
        query.establishmentId = establishmentId
      }

      const redirectFromQuery = normalizeRouteQueryParam(to.query.redirect)
      if (redirectFromQuery) {
        query.redirect = redirectFromQuery
      } else if (to.path === "/checkout" || to.name === "checkout-flow") {
        query.redirect = "checkout"
      } else if (to.path === "/editorial-calendar" || to.name === "editorial-calendar") {
        query.redirect = "editorial-calendar"
      } else if (to.path === "/notifications" || to.name === "notification-center") {
        query.redirect = "notifications"
      } else if (to.path.startsWith("/mes-echanges") || to.path.startsWith("/dialogue")) {
        query.redirect = "mes-echanges"
      }

      const source = dialogueSource ?? normalizeRouteQueryParam(to.query.source)
      if (source) {
        query.source = source
      }

      return next({ name: "magic-link", query })
    }
  }

  if (!["error-version", "auth"].includes(to.name)) {
    try {
      await loadCredentials(to)
    } catch (error) {
      // Ne pas bloquer la navigation en cas d'erreur
      console.error('[Router Guard] Error loading credentials:', error)
    }
  }

  // Après avoir chargé les credentials, vérifier si on doit rediriger vers /configuration
  // pour les comptes non onboardés qui arrivent via Nova ou magic link
  const authStore = useAuthenticationStore()
  if (to.query.token && authStore.user?.account && !authStore.user.account.configured) {
    const configState = authStore.user.account.configuration_state
    const isDialoguePath =
      to.path.startsWith("/mes-echanges") || to.path.startsWith("/dialogue")
    
    // Ne pas rediriger si on est déjà sur une route de configuration, d'authentification ou Mes échanges
    if (
      !isDialoguePath &&
      to.path !== '/configuration' &&
      !to.path.startsWith('/configuration/') &&
      !to.path.startsWith('/welcome/')
    ) {
      // Les comptes DEMO ne sont pas configurés mais ont accès à l'app
      if (configState !== ConfigurationState.DEMO) {
        return next('/configuration')
      }
    }
  }

  // Rediriger les établissements DEMO de la page d'accueil vers le calendrier éditorial
  if (to.name === 'home') {
    const establishmentStore = useEstablishmentStore()
    const isDemoEstablishment = getAccessPlanCode(establishmentStore.currentEstablishment) === 'DEMO'
    
    if (isDemoEstablishment) {
      return next({ name: 'editorial-calendar' })
    }
  }

  const routeName = to.name as string | undefined
  const pendingDialogueSource =
    dialogueSource ?? sessionStorage.getItem("requestedDialogueSource")

  if (
    routeName &&
    DIALOGUE_ROUTE_NAMES.includes(routeName) &&
    pendingDialogueSource &&
    localStorage.getItem("token")
  ) {
    useSpaceStore().setSpace(Space.DIALOGUE)
  }

  const spaceRedirection = resolveSpaceRedirection(to)
  if (spaceRedirection) {
    return next(spaceRedirection)
  }

  if (
    routeName &&
    DIALOGUE_ROUTE_NAMES.includes(routeName) &&
    pendingDialogueSource &&
    !dialogueSource &&
    localStorage.getItem("token")
  ) {
    return next({
      ...to,
      query: {
        ...to.query,
        source: pendingDialogueSource,
      },
    })
  }

  if (isReviewGatedRoute(to) && localStorage.getItem("token")) {
    try {
      await ensureReviewRouteContext()
    } catch (error) {
      console.error("[Router Guard] Error loading review route context:", error)
    }
  }

  let path = handleMiddlewares(to, next)

  // Éviter les boucles infinies : si le middleware veut rediriger vers le même path, ne pas rediriger
  if (path && (to.path === path || to.fullPath === path)) {
    path = null
  }

  if (to.name !== "error-version" && to.meta.middleware && !to.meta.middleware.includes(auth)) {
    if (to.path !== "/error") {
      path = "/error"
    }

    if (to.path === "/error") {
      path = "/"
    }
  }

  changePageTitle(to)

  return next(path)
})

/** Routes atteignables depuis les deux espaces : elles ne doivent pas en forcer un. */
const SPACE_AGNOSTIC_ROUTE_NAMES = [
  "maj-form",
  "adh-form",
  "notification-center",
  "user-profil",
  "user-management",
  "user-management-details",
  "user-create",
]

function isSpaceAgnosticRoute(routeName: string) {
  return routeName.startsWith("settings-") || SPACE_AGNOSTIC_ROUTE_NAMES.includes(routeName)
}

/**
 * Maintient l'espace courant cohérent avec la route demandée et renvoie l'utilisateur
 * vers l'espace exploitable quand la destination n'a rien à afficher.
 */
function resolveSpaceRedirection(to: RouteLocationNormalized) {
  if (!localStorage.getItem("token")) {
    return null
  }

  const routeName = to.name as string | undefined
  if (!routeName || isSpaceAgnosticRoute(routeName)) {
    return null
  }

  const spaceStore = useSpaceStore()
  const accountStore = useAccountStore()

  if (DIALOGUE_ROUTE_NAMES.includes(routeName)) {
    if (accountStore.establishmentLoaded && !accountStore.hasDestinationProviders) {
      return { name: "home" }
    }

    spaceStore.setSpace(Space.DIALOGUE)

    return null
  }

  spaceStore.setSpace(Space.PILOTAGE)

  const hasNoEstablishment =
    accountStore.establishmentLoaded && accountStore.establishments.length === 0

  if (routeName === "home" && hasNoEstablishment) {
    const pendingSource = sessionStorage.getItem("requestedDialogueSource")

    if (accountStore.hasDestinationProviders) {
      return pendingSource
        ? { name: "dialogue-home", query: { source: pendingSource } }
        : { name: "dialogue-home" }
    }

    return { name: "pilotage-empty-state" }
  }

  if (
    hasNoEstablishment &&
    (routeName === "editorial-calendar" || routeName === "demo-review-responses")
  ) {
    return { name: "pilotage-empty-state" }
  }

  return null
}

function normalizeRouteQueryParam(value: unknown): string | undefined {
  if (value == null) {
    return undefined
  }

  if (Array.isArray(value)) {
    return value[0] != null ? String(value[0]) : undefined
  }

  return String(value)
}

async function loadLanguages() {
  let locale = i18n.global.locale.value
  if (locale === undefined) {
    locale = import.meta.env.VITE_APP_I18N_LOCALE
  }

  return loadLanguageAsync(locale as string)
}

function handleMiddlewares(to: RouteLocationNormalized, next: NavigationGuardNext) {
  const middlewares = to.meta.middleware as any

  let path = null
  let middlewareBlock = false
  let errorPath = ""

  if (middlewares) {
    middlewares.forEach((middleware: (arg0: any) => any) => {
      if (!middlewareBlock) {
        const currentMiddleware = middleware(next)
        if (currentMiddleware && currentMiddleware.success === false) {
          // Éviter les boucles infinies : ne pas rediriger si on est déjà sur le path de redirection
          const redirectPath = currentMiddleware.path
          
          // Vérifier si on est déjà sur le path de redirection
          const isAlreadyOnRedirectPath = to.path === redirectPath || 
                                         to.fullPath === redirectPath
          
          if (!isAlreadyOnRedirectPath) {
            middlewareBlock = true
            errorPath = redirectPath
          }
        }
      }
    })
  }

  if (middlewareBlock) {
    path = errorPath
  }

  return path
}

/**
 * Modifier le titre de l'onglet du navigateur de la page courante
 */
function changePageTitle(to: RouteLocationNormalized) {
  const DEFAULT_TITLE = "common.appname"
  if (to.meta.title) {
    const params = to.meta.hasTitleParams as IPageTitleParams
    if (params) {
      /**
       * Le paramètre de type param permet d'utiliser la valeur de la requête dans le titre
       */
      if (params.type === PageTitleParamsType.PARAM) {
        document.title = i18n.global.t(to.meta.title!.toString(), {
          [params.name]: convertSlugToString(to.params[params.name.toString()].toString()),
        })
      } else if (params.type === PageTitleParamsType.KEY) {
        /**
         * Le paramètre de type key permet d'utiliser la valeur de la requête dans le chemin de la traduction i18n
         */
        document.title = i18n.global.t(to.meta.title!.toString() + "." + to.params[params.name.toString()])
      }
    } else {
      document.title = i18n.global.t(to.meta.title!.toString())
    }
  } else {
    document.title = i18n.global.t(DEFAULT_TITLE)
  }
}

async function loadCredentials(to: RouteLocationNormalized) {
  const authStore = useAuthenticationStore()
  const establishmentStore = useEstablishmentStore()

  if (to.query.token !== undefined) {
    const token = to.query.token!.toString()
    
    // Vérifier si c'est un UUID (magic link token) ou un JWT
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    
    if (uuidRegex.test(token)) {
      // C'est un magic link token, ne pas le traiter ici
      // La route /magic-link s'en occupera
      return
    }
    
    // C'est un JWT, traitement normal
    const currentToken = localStorage.getItem("token")
    const isNewToken = currentToken !== token
    const forceLogin = to.query.force_login === 'true'

    if (forceLogin) {
      markNovaLoginSessionFromUrlIfPresent()
    }
    
    // Si c'est un nouveau token ou si force_login=true, nettoyer l'ancien état
    if (isNewToken || forceLogin) {
      // Vérifier si on vient déjà d'un rechargement (éviter la boucle infinie)
      const isReload = sessionStorage.getItem('tokenReload') === 'true'
      
      if (!isReload) {
        // Marquer qu'on va recharger
        sessionStorage.setItem('tokenReload', 'true')
        
        // Stocker le nouveau token d'abord
        localStorage.setItem("token", token)
        localStorage.setItem("tokenType", "Bearer")
        
        // Stocker l'établissement si fourni
        const establishmentId = to.query.establishmentId?.toString()
        if (establishmentId) {
          sessionStorage.setItem("requestedEstablishmentId", establishmentId)
        }
        
        // Recharger la page pour éviter tous les conflits de concurrence
        window.location.reload()
        return
      } else {
        // On vient d'un rechargement, nettoyer le flag et continuer normalement
        sessionStorage.removeItem('tokenReload')
      }
    }
    
    // Stocker le nouveau token APRÈS le nettoyage
    localStorage.setItem("token", token)
    localStorage.setItem("tokenType", "Bearer")
    
    // Vérifier si un establishmentId est fourni dans l'URL et le stocker
    const establishmentId = to.query.establishmentId?.toString()
    if (establishmentId) {
      sessionStorage.setItem("requestedEstablishmentId", establishmentId)
    }

    // Attendre un peu plus pour s'assurer que le token est bien stocké
    await new Promise(resolve => setTimeout(resolve, 100))
    
    try {
      await loadUser()
      await establishmentStore.initEstablishment(establishmentId)
    } catch (error) {
      // Ne pas bloquer la navigation en cas d'erreur
      console.error('[loadCredentials] Error loading user or establishment:', error)
    }
  } else if (
    to.meta !== undefined &&
    to.meta.middleware !== undefined &&
    to.meta.middleware!.includes(auth) &&
    localStorage.getItem("token")
  ) {
    try {
      await loadUser()
    } catch (error) {
      console.error("[loadCredentials] Error loading user:", error)
    }
  }
}

let loadUserPromise: Promise<void> | null = null

function isReviewGatedRoute(to: RouteLocationNormalized): boolean {
  return to.path.startsWith("/reviews") || to.path.startsWith("/knowledge-base")
}

async function ensureReviewRouteContext(): Promise<void> {
  await loadUser()

  const establishmentStore = useEstablishmentStore()
  const establishmentId = establishmentStore.currentEstablishment?.id

  if (!establishmentId) {
    await new Promise<void>((resolve) => {
      establishmentStore.initEstablishment(undefined, () => resolve())
    })
    return
  }

  const contract = getAccessContract(establishmentStore.currentEstablishment)
  const hasPlanFeatures = (contract?.product_plan?.access_features?.length ?? 0) > 0

  if (!hasPlanFeatures) {
    await establishmentStore.getCurrentEstablishment(Number(establishmentId))
  }

  establishmentStore.syncLocalDiffuserBranding()
}

async function loadUser(): Promise<void> {
  if (loadUserPromise) {
    return loadUserPromise
  }

  const authStore = useAuthenticationStore()

  loadUserPromise = (async () => {
  try {
    const accountStore = useAccountStore()
    const env = import.meta.env.VITE_ENV

    const callback = () => {
      if (!authStore.user) {
        return
      }
      
      const userConf = {
        user_id: "uska-" + authStore.user.id,
        name: authStore.user.first_name + " " + authStore.user.last_name,
        email: authStore.user.email,
        role: authStore.user.role?.code || '',
        account_configured: authStore.user.account?.configured ?? true,
      }

      setTimeout(() => {
        if (window.intercomSettings && accountStore.hasPremium && !authStore.isSaleOnly) {
          Object.assign(window.intercomSettings, userConf)

          window.Intercom("update", window.intercomSettings)
        }
      }, 2000)

      if (env === "production") {
        Sentry.setContext("user", {
          id: authStore.user.id,
          email: authStore.user.email,
        })
      }
    }
    
    const meResult = authStore.me(callback)
    
    // S'assurer que authStore.me() retourne toujours une Promise
    if (meResult && typeof meResult.then === 'function') {
      await meResult
    }
  } catch (error) {
    // Ne pas propager l'erreur, juste la logger
    console.error('[loadUser] Error:', error)
  }
  })()

  try {
    await loadUserPromise
  } finally {
    loadUserPromise = null
  }
}


function convertSlugToString(text: String): String {
  return text.replace(/-/, " ")
}

export default router
